import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { galleryImages } from "@/data/projects";
import { galleryVideos } from "@/data/videos";
import { X, ChevronLeft, ChevronRight, Play, ExternalLink, Video, Image as ImageIcon } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export default function Gallery() {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);

  const categories = [
    { value: "all", label: "All" },
    { value: "studio", label: "Studio Facilities" },
    { value: "production", label: "Production" },
    { value: "post-production", label: "Post-Production" },
    { value: "behind-scenes", label: "Behind the Scenes" },
  ];

  const filteredImages = selectedCategory === "all" 
    ? galleryImages 
    : galleryImages.filter(img => img.category === selectedCategory);

  const handlePrevious = () => {
    if (selectedImage !== null && selectedImage > 0) {
      setSelectedImage(selectedImage - 1);
    }
  };

  const handleNext = () => {
    if (selectedImage !== null && selectedImage < filteredImages.length - 1) {
      setSelectedImage(selectedImage + 1);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "ArrowLeft") handlePrevious();
    if (e.key === "ArrowRight") handleNext();
    if (e.key === "Escape") {
      setSelectedImage(null);
      setSelectedVideo(null);
    }
  };

  const getEmbedUrl = (url: string, platform: string) => {
    if (platform === 'youtube') {
      const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?]+)/);
      return match ? `https://www.youtube.com/embed/${match[1]}?autoplay=1&rel=0` : '';
    } else {
      // Handle Vimeo URLs with or without hash tokens
      const match = url.match(/vimeo\.com\/(?:manage\/videos\/)?(\d+)(?:\/([a-zA-Z0-9]+))?/);
      if (match) {
        const videoId = match[1];
        const hash = match[2];
        return hash 
          ? `https://player.vimeo.com/video/${videoId}?h=${hash}&autoplay=1&title=0&byline=0&portrait=0`
          : `https://player.vimeo.com/video/${videoId}?autoplay=1&title=0&byline=0&portrait=0`;
      }
      return '';
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-30">
          <img 
            src={galleryImages[0].image} 
            alt="Gallery" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              Gallery
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8">
              Videos & Images - Behind the Scenes, Trailers, and Studio Facilities
            </p>
            
            {/* Jump Navigation Buttons */}
            <div className="flex flex-wrap items-center justify-center gap-4 mt-8">
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                onClick={() => {
                  const videoSection = document.getElementById('videos-section');
                  videoSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="group flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <Video className="w-5 h-5 group-hover:scale-110 transition-transform" />
                <span className="font-semibold">Jump to Videos</span>
              </motion.button>
              
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
                onClick={() => {
                  const imageSection = document.getElementById('images-section');
                  imageSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="group flex items-center gap-2 px-6 py-3 rounded-full bg-accent text-accent-foreground hover:bg-accent/90 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <ImageIcon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                <span className="font-semibold">Jump to Images</span>
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Videos Section */}
      <section id="videos-section" className="py-24 bg-muted/30 scroll-mt-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              Videos
            </h2>
            <p className="text-xl text-muted-foreground">
              Behind the Scenes, Trailers, and Exclusive Content
            </p>
          </motion.div>

          <Tabs defaultValue="all" className="w-full">
            <TabsList className="grid w-full max-w-4xl mx-auto grid-cols-6 mb-12">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="trailers">Trailers</TabsTrigger>
              <TabsTrigger value="behind-scenes">Behind Scenes</TabsTrigger>
              <TabsTrigger value="character-intro">Characters</TabsTrigger>
              <TabsTrigger value="songs">Songs</TabsTrigger>
              <TabsTrigger value="profile">Profile</TabsTrigger>
            </TabsList>

            {['all', 'trailers', 'behind-scenes', 'character-intro', 'songs', 'profile', 'events'].map((category) => (
              <TabsContent key={category} value={category}>
                <motion.div 
                  layout
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                >
                  {galleryVideos
                    .filter(video => category === 'all' || video.category === category)
                    .map((video) => {
                      const getVideoId = (url: string, platform: string) => {
                        if (platform === 'youtube') {
                          const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?]+)/);
                          return match ? match[1] : '';
                        } else {
                          const match = url.match(/vimeo\.com\/(?:manage\/videos\/)?(\d+)/);
                          return match ? match[1] : '';
                        }
                      };

                      const videoId = getVideoId(video.url, video.platform);
                      const thumbnail = video.platform === 'youtube' 
                        ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
                        : `https://vumbnail.com/${videoId}.jpg`;

                      return (
                        <motion.div
                          key={video.id}
                          initial={{ opacity: 0, y: 24 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4 }}
                          className="group relative bg-card rounded-xl overflow-hidden border border-border hover:border-primary/50 transition-all duration-300 cursor-pointer"
                          style={{
                            boxShadow: "0 4px 20px -4px color-mix(in srgb, var(--primary) 10%, transparent)",
                          }}
                          onClick={() => setSelectedVideo(video.url)}
                        >
                          <div className="relative aspect-video overflow-hidden bg-muted">
                            <img
                              src={thumbnail}
                              alt={video.title}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = video.platform === 'youtube' 
                                  ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
                                  : 'https://via.placeholder.com/640x360?text=Video';
                              }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-16 h-16 rounded-full bg-primary/90 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                <Play className="w-8 h-8 text-primary-foreground ml-1" fill="currentColor" />
                              </div>
                            </div>

                            <div className="absolute top-3 right-3">
                              <Badge variant="secondary" className="backdrop-blur-sm">
                                {video.platform === 'youtube' ? 'YouTube' : 'Vimeo'}
                              </Badge>
                            </div>
                          </div>

                          <div className="p-4">
                            <h3 className="text-lg font-semibold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                              {video.title}
                            </h3>
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {video.description}
                            </p>
                          </div>
                        </motion.div>
                      );
                    })}
                </motion.div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Images Gallery Grid */}
      <section id="images-section" className="py-24 scroll-mt-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              Images
            </h2>
            <p className="text-xl text-muted-foreground">
              Studio Facilities & Production Photos
            </p>
          </motion.div>

          <Tabs defaultValue="all" className="w-full" onValueChange={setSelectedCategory}>
            <TabsList className="grid w-full max-w-4xl mx-auto grid-cols-5 mb-12">
              {categories.map((category) => (
                <TabsTrigger key={category.value} value={category.value}>
                  {category.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value={selectedCategory}>
              <motion.div 
                layout
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              >
                <AnimatePresence mode="popLayout">
                  {filteredImages.map((image, index) => (
                    <motion.div
                      key={image.id}
                      layout
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.3 }}
                      className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer border border-border"
                      onClick={() => setSelectedImage(index)}
                      style={{
                        boxShadow: "0 4px 12px color-mix(in srgb, var(--primary) 10%, transparent)",
                      }}
                    >
                      <img
                        src={image.image}
                        alt={image.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      
                      <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                        <h3 className="text-sm font-semibold text-foreground mb-1">
                          {image.title}
                        </h3>
                        {image.description && (
                          <p className="text-xs text-muted-foreground line-clamp-2">
                            {image.description}
                          </p>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </motion.div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Video Player Dialog */}
      <Dialog open={selectedVideo !== null} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent 
          className="max-w-7xl w-full h-[90vh] p-0 bg-background/95 backdrop-blur-xl border-border"
        >
          {selectedVideo !== null && (
            <div className="relative w-full h-full flex items-center justify-center p-4">
              <button
                onClick={() => setSelectedVideo(null)}
                className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-background/80 backdrop-blur-sm border border-border flex items-center justify-center hover:bg-background transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="relative w-full h-full flex flex-col items-center justify-center gap-4">
                <iframe
                  src={getEmbedUrl(selectedVideo, galleryVideos.find(v => v.url === selectedVideo)?.platform || 'youtube')}
                  className="w-full h-full rounded-lg"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  onError={() => {
                    // If iframe fails to load, open in new tab
                    const videoUrl = selectedVideo.replace('/manage/videos/', '/');
                    window.open(videoUrl, '_blank');
                    setSelectedVideo(null);
                  }}
                />
                {selectedVideo.includes('/manage/videos/') && (
                  <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-background/90 backdrop-blur-sm px-6 py-3 rounded-lg border border-border">
                    <p className="text-sm text-muted-foreground text-center mb-2">
                      Video not loading?
                    </p>
                    <Button
                      size="sm"
                      onClick={() => {
                        const videoUrl = selectedVideo.replace('/manage/videos/', '/');
                        window.open(videoUrl, '_blank');
                        setSelectedVideo(null);
                      }}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Watch on Vimeo
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Lightbox Dialog */}
      <Dialog open={selectedImage !== null} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent 
          className="max-w-7xl w-full h-[90vh] p-0 bg-background/95 backdrop-blur-xl border-border"
          onKeyDown={(e: any) => handleKeyDown(e)}
        >
          {selectedImage !== null && (
            <div className="relative w-full h-full flex items-center justify-center p-4">
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-background/80 backdrop-blur-sm border border-border flex items-center justify-center hover:bg-background transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              {selectedImage > 0 && (
                <button
                  onClick={handlePrevious}
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 rounded-full bg-background/80 backdrop-blur-sm border border-border flex items-center justify-center hover:bg-background transition-colors"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
              )}

              {selectedImage < filteredImages.length - 1 && (
                <button
                  onClick={handleNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 rounded-full bg-background/80 backdrop-blur-sm border border-border flex items-center justify-center hover:bg-background transition-colors"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              )}

              <div className="relative w-full h-full flex flex-col items-center justify-center">
                <img
                  src={filteredImages[selectedImage].image}
                  alt={filteredImages[selectedImage].title}
                  className="max-w-full max-h-[calc(100%-100px)] object-contain rounded-lg"
                />
                
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-background to-transparent">
                  <div className="max-w-4xl mx-auto text-center">
                    <h3 className="text-2xl font-bold text-foreground mb-2">
                      {filteredImages[selectedImage].title}
                    </h3>
                    {filteredImages[selectedImage].description && (
                      <p className="text-muted-foreground">
                        {filteredImages[selectedImage].description}
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground mt-2">
                      {selectedImage + 1} / {filteredImages.length}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}